// src/features/maintenance/MarkCompleteDialog.jsx — records a service for one plan × vehicle (spec §8.2).
// Pre-fills from the server's resolved reading (vehicle odometer first, tracker otherwise) and says which it is.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckSquare, Loader2 } from 'lucide-react';
import { maintenancePlans } from '@/lib/trackizy';

const input = 'w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10';
const label = 'block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1';

export default function MarkCompleteDialog({ row, onClose, onSaved }) {
  const { t } = useTranslation();
  const [odometer, setOdometer] = useState(row.reading?.km != null ? String(row.reading.km) : '');
  const [hours, setHours] = useState(row.reading?.hours != null ? String(row.reading.hours) : '');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const src = (s) => (s === 'vehicle' ? t('maintenancePlans.sourceVehicle', 'from the vehicle') : s === 'tracker' ? t('maintenancePlans.sourceTracker', 'from the tracker') : null);

  useEffect(() => { const onKey = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey); }, [onClose]);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError(null);
    try {
      await maintenancePlans.recordEvent({ plan_id: row.planId, device_id: row.deviceId, odometer_km: odometer !== '' ? Number(odometer) : null, engine_hours: hours !== '' ? Number(hours) : null, cost: cost !== '' ? Number(cost) : null, notes: notes.trim() || null });
      onSaved();
    } catch (err) { setError(err?.message || 'Failed to save'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">{t('maintenance.markComplete', 'Mark complete')}</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{row.planName} — {row.deviceName}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>{t('maintenancePlans.odometerKm', 'Odometer (km)')}</label>
              <input data-testid="complete-odometer" type="number" min="0" step="any" value={odometer} onChange={(e) => setOdometer(e.target.value)} className={input} /></div>
            <div><label className={label}>{t('maintenancePlans.engineHours', 'Engine hours')}</label>
              <input data-testid="complete-hours" type="number" min="0" step="any" value={hours} onChange={(e) => setHours(e.target.value)} className={input} /></div>
          </div>
          {(row.reading?.kmSource || row.reading?.hoursSource) && (
            <p data-testid="complete-source" className="-mt-2 text-[11px] text-slate-400 dark:text-slate-500">
              {[row.reading.kmSource && `${t('maintenancePlans.odometerKm', 'Odometer (km)')} ${src(row.reading.kmSource)}`, row.reading.hoursSource && `${t('maintenancePlans.engineHours', 'Engine hours')} ${src(row.reading.hoursSource)}`].filter(Boolean).join(' · ')}
            </p>
          )}
          <div><label className={label}>{t('maintenance.cost', 'Cost')}</label>
            <input data-testid="complete-cost" type="number" min="0" step="any" value={cost} onChange={(e) => setCost(e.target.value)} className={input} /></div>
          <div><label className={label}>{t('maintenance.notes', 'Notes')}</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={`${input} h-auto py-2 resize-none`} /></div>
          {error && <p className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded-xl px-3 py-2">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="h-9 px-4 text-xs font-semibold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800">{t('common.cancel', 'Cancel')}</button>
            <button data-testid="complete-submit" type="submit" disabled={saving} className="h-9 px-4 text-xs font-semibold rounded-xl text-white disabled:opacity-60 flex items-center gap-1.5" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckSquare size={13} />}{t('maintenance.markComplete', 'Mark complete')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
